import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  withdrawalReportService,
  WithdrawalReportStatusDataType,
} from 'commons/services/withdrawalReportService';
import { ReportStatus } from 'commons/types';

const REFETCH_FREQUENCY = 1000; // 1 second
const MAX_REFETCH_COUNT = 20; // max 20 seconds

export function useGetWithdrawalReportStatusPolling({
  requestId,
  attemptId,
  onFinished,
  onError,
}: {
  requestId?: number;
  attemptId?: number;
  onFinished?: (data: WithdrawalReportStatusDataType) => void;
  onError?: () => void;
}) {
  const [isPolling, setIsPolling] = useState(false);
  const onceKey = attemptId ?? requestId;
  const finishedForKeyRef = useRef<number | undefined>(undefined);

  const reportStatusAfterPolling = (status?: ReportStatus) => {
    return !status || status === ReportStatus.Pending
      ? ReportStatus.Failed
      : status;
  };

  const getReportStatusQuery = useQuery({
    queryKey: ['getWithdrawalReportStatusPolling', requestId, attemptId],
    queryFn: () => {
      if (!requestId) return;

      setIsPolling(true);
      return withdrawalReportService.getWithdrawalReportStatus(requestId);
    },
    enabled: !!requestId,
    onError: () => {
      if (requestId === undefined || finishedForKeyRef.current === onceKey) {
        return;
      }
      finishedForKeyRef.current = onceKey;
      setIsPolling(false);
      onError?.();
    },
    refetchInterval(data, query) {
      const isError = query.state.status === 'error';
      const shouldRefetch =
        !isError &&
        (!data ||
          (data.status === ReportStatus.Pending &&
            query.state.dataUpdateCount <= MAX_REFETCH_COUNT));

      if (shouldRefetch) {
        return REFETCH_FREQUENCY;
      }

      if (
        !isError &&
        requestId !== undefined &&
        finishedForKeyRef.current !== onceKey
      ) {
        finishedForKeyRef.current = onceKey;
        setIsPolling(false);

        if (data && onFinished) {
          onFinished({
            status: reportStatusAfterPolling(data.status),
            temporaryDownloadUrl: data.temporaryDownloadUrl,
          });
        }
      }

      return false;
    },
  });

  return {
    isPolling,
    query: getReportStatusQuery,
  };
}
